import { useSession } from "@hooks/useSession";
import { queriesAccounts } from "@query";
import { sessionService } from "@services/sessionService";
import {
  useIsMobile,
  WSAvatar,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useState } from "react";
import styles from "../../../../components/SideNav/other.module.scss";
import { Tooltip } from "../../../../components/Tooltip";
import { selectorAccountDisplayName } from "../../../../shared/selectors/selectorAccountDisplayName";
import { CatchClickOutside } from "../../../../utils/useDidClickOutside";

export interface SwitchAccountsProps {
  isSideNavOpened: boolean;
  setIsSideNavOpened: (isDrawerOpened: boolean) => void;
}

export const SwitchAccounts: React.FC<SwitchAccountsProps> = ({
  isSideNavOpened,
  setIsSideNavOpened
}) => {
  const currentAccountId = useSession().accountId;
  const queryAccounts = queriesAccounts.listAccounts.useQuery();
  const [isSwitching, setIsSwitching] = useState(false);

  const onSwitchAccount = async (accountId: string) => {
    if (isSwitching || accountId === currentAccountId) {
      return;
    }
    setIsSwitching(true);
    try {
      await sessionService.switchAccount(accountId, {
        reload: true
      });
    } finally {
      setIsSwitching(false);
    }
  };

  const accounts = queryAccounts.data?.data ?? [];
  if (accounts.length === 0) {
    return null;
  }

  const currentAccount = accounts.find(
    account => account.id === currentAccountId
  );
  const currentAccountName = currentAccount
    ? selectorAccountDisplayName(currentAccount)
    : "Select account";

  return (
    <Dropdown
      label={currentAccountName}
      setIsSideNavOpened={setIsSideNavOpened}
      isSideNavOpened={isSideNavOpened}
    >
      {accounts.map(account => {
        const name = selectorAccountDisplayName(account);

        return (
          <WSFlexBox.CenterY
            key={account.id}
            onClick={() => onSwitchAccount(account.id!)}
            className={styles.dropdownItem}
            px="M"
            wrap="nowrap"
            title={name}
            data-testid={account.id}
          >
            <WSAvatar.Text size="S" text={name.charAt(0)} mr="M" />
            <WSText.ParagraphSm color="gray600" className={styles.ellipsis}>
              {name}
              {account.id === currentAccountId ? " (current)" : ""}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        );
      })}
    </Dropdown>
  );
};

interface DropdownProps extends WSElementProps {
  logo?: string;
  label: string;
  isSideNavOpened: boolean;
  setIsSideNavOpened: (isDrawerOpened: boolean) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  children,
  isSideNavOpened,
  setIsSideNavOpened
}) => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isSideNavOpened) {
      setIsExpanded(false);
    }
  }, [isSideNavOpened]);

  return (
    <>
      {isSideNavOpened || isMobile ? (
        <WSElement className={styles.dropdownWrapper} px="XL" mb="XL">
          <WSFlexBox
            className={styles.trigger}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
            data-testid="switchAccountMenu"
          >
            <WSFlexBox.CenterY wrap="nowrap" title={label}>
              <WSAvatar.Text size="S" text={label.charAt(0)} />
              <WSText.ParagraphSm
                ml="M"
                color="gray600"
                className={styles.ellipsis}
              >
                {label}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
            <WSIcon
              block
              name={isExpanded ? "caret-up" : "caret-down"}
              size="XS"
              color="gray700"
              data-testid="menu-arrow-indicator"
            />
            {isExpanded ? (
              <CatchClickOutside onClickOutside={() => setIsExpanded(false)}>
                {ref => (
                  <div ref={ref} className={styles.dropdownContent}>
                    {children}
                  </div>
                )}
              </CatchClickOutside>
            ) : null}
          </WSFlexBox>
        </WSElement>
      ) : (
        <Tooltip
          className={styles.tooltip}
          onTriggerClick={() => {
            setIsSideNavOpened(true);
            setIsExpanded(true);
          }}
          trigger={
            <WSFlexBox.Center>
              <WSAvatar.Text size="S" text={label.charAt(0)} />
            </WSFlexBox.Center>
          }
          placement="right-start"
        >
          <WSText color="white">Switch account</WSText>
        </Tooltip>
      )}
    </>
  );
};
